import StatCard from "../../../../parts/cards/StatCard";
import { formatNumber } from "../../../../utils/format-sub";
import CsvExportLink from "./CsvExportLink";
import GrowthSection from "./GrowthSection";
import { rowsToCsv } from "./incomeChartData";

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
// "▲ +9" / "▼ -3" in success/danger; a flat delta stays plain.
const Delta = ({ value }) => (
  <span
    className={
      value > 0 ? "text-(--success)" : value < 0 ? "text-(--danger)" : ""
    }
  >
    {value > 0 ? `▲ +${value}` : value < 0 ? `▼ ${value}` : value}
  </span>
);

/**
 * Requirements §5.7 / §6.3: free trials, measured in completed workouts,
 * not logins. Tiles first (snapshot + period, each tagged), then the
 * started → 1st workout → 3rd workout → converted funnel, then conversion
 * segmented by workouts completed — every PUSH plan is all-access, so the
 * plan a trialist picks says nothing; what they did during the trial does.
 * Correlational, not causal, and the footnote says so.
 */
const GrowthTrialFunnel = ({ data, currency, badges, t }) => {
  if (!data) return null;
  const {
    onTrialNow = 0,
    mrrIfAllConvert = 0,
    startedInPeriod = 0,
    endedInPeriod = 0,
    convertedCount = 0,
    firstPaymentFailedCount = 0,
    lapsedCount = 0,
    convertedOfStarted,
    convertedOfEnded,
    mrrAdded = 0,
    funnel = { started: 0, firstWorkout: 0, thirdWorkout: 0 },
    bands = [],
    previous = null,
  } = data;
  const money = (v) => formatNumber(v || 0, currency);
  const rate = (v) => (v == null ? "—" : `${v}%`);

  // Captions are nodes so the arrow can carry colour without the i18n
  // string knowing about markup: "{{delta}} vs previous period" is split
  // around a placeholder and the coloured <Delta> dropped in.
  const withDelta = (key, value) => {
    const [before, after] = t(key, { delta: "\u0000" }).split("\u0000");
    return (
      <>
        {before}
        <Delta value={value} />
        {after}
      </>
    );
  };
  const startedDelta = previous
    ? withDelta(
        "growth.vsPrev",
        startedInPeriod - (previous.startedInPeriod || 0)
      )
    : null;
  const rateDelta =
    previous && convertedOfEnded != null && previous.convertedOfEnded != null
      ? withDelta(
          "growth.ptsVsPrev",
          Math.round((convertedOfEnded - previous.convertedOfEnded) * 10) / 10
        )
      : null;

  const funnelSteps = [
    { key: "started", value: funnel.started, caption: null },
    {
      key: "firstWorkout",
      value: funnel.firstWorkout,
      caption: t("growth.trials.neverTrained", {
        pct: pct(funnel.firstWorkout, funnel.started),
        count: funnel.started - funnel.firstWorkout,
      }),
    },
    {
      key: "thirdWorkout",
      value: funnel.thirdWorkout,
      caption: t("growth.trials.stalled", {
        pct: pct(funnel.thirdWorkout, funnel.started),
        count: funnel.firstWorkout - funnel.thirdWorkout,
      }),
    },
    {
      key: "converted",
      value: convertedCount,
      caption: t("growth.trials.ofStartedOfEnded", {
        ofStarted: rate(convertedOfStarted),
        ofEnded: rate(convertedOfEnded),
      }),
    },
  ];

  const csv = rowsToCsv(
    [
      t("growth.trials.band"),
      t("growth.trials.trialsEnded"),
      t("growth.trials.converted"),
      t("growth.trials.lapsed"),
      t("growth.trials.rate"),
      t("growth.trials.mrrAddedCol"),
    ],
    bands.map((b) => [
      t(`growth.trials.bands.${b.label}`),
      b.trialsEnded,
      b.converted,
      b.lapsed,
      b.conversionRate ?? "",
      b.mrrAdded,
    ])
  );

  return (
    <GrowthSection
      title={t("growth.trials.title")}
      subtitle={t("growth.trials.subtitle")}
      tiles={
        <>
          <StatCard
            title={t("growth.trials.onTrialNow")}
            badge={badges.now}
            current={onTrialNow}
            caption={t("growth.trials.mrrIfAllConvert", {
              amount: money(mrrIfAllConvert),
            })}
          />
          <StatCard
            title={t("growth.trials.started")}
            badge={badges.period}
            current={startedInPeriod}
            caption={startedDelta || undefined}
          />
          <StatCard
            title={t("growth.trials.ended")}
            badge={badges.period}
            current={endedInPeriod}
            caption={t("growth.trials.endedCaption", {
              converted: convertedCount,
              failed: firstPaymentFailedCount,
              lapsed: lapsedCount,
            })}
          />
          <StatCard
            title={t("growth.trials.conversionRate")}
            badge={badges.period}
            current={convertedOfEnded}
            formatValue={rate}
            caption={
              <>
                {rateDelta}
                {rateDelta && " · "}
                {t("growth.trials.mrrAdded", { amount: money(mrrAdded) })}
              </>
            }
          />
        </>
      }
    >
      <header className="growth-section__header mt-6">
        <h3>{t("growth.trials.funnelTitle")}</h3>
        <p>{t("growth.trials.funnelSubtitle")}</p>
        {badges.period}
      </header>
      <div className="stat-band mt-0!">
        <div className="stat-cards">
          {funnelSteps.map((step) => (
            <article
              key={step.key}
              className="funnel-tile"
              data-converted={step.key === "converted" || undefined}
            >
              <header>
                <p>
                  <b>{t(`growth.trials.funnel.${step.key}`)}</b>
                </p>
              </header>
              <h3>{step.value}</h3>
              <p className="stat-card__caption">{step.caption || "—"}</p>
              <div className="funnel-tile__bar">
                <span
                  style={{ width: `${pct(step.value, funnel.started)}%` }}
                />
              </div>
            </article>
          ))}
        </div>
      </div>

      <section className="incomes-container growth-card mt-6">
        <header className="table-card__header">
          <h3>{t("growth.trials.tableTitle")}</h3>
          {badges.period}
          <span className="grow" />
          <CsvExportLink csv={csv} filename="trial-conversion.csv" t={t} />
        </header>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
        <table role="report-table">
          <thead>
            <tr>
              <th>{t("growth.trials.band")}</th>
              <th>{t("growth.trials.trialsEnded")}</th>
              <th>{t("growth.trials.converted")}</th>
              <th>{t("growth.trials.lapsed")}</th>
              <th>{t("growth.trials.rate")}</th>
              <th>{t("growth.trials.mrrAddedCol")}</th>
            </tr>
          </thead>
          <tbody>
            {bands.map((b) => (
              <tr key={b.label}>
                <td>
                  {t(`growth.trials.bands.${b.label}`)}
                  {b.label === "0" && (
                    <small className="table-card__subtitle">
                      {" "}
                      {t("growth.trials.bandNoneHint")}
                    </small>
                  )}
                </td>
                <td>{b.trialsEnded}</td>
                <td>{b.converted}</td>
                <td>{b.lapsed}</td>
                <td>
                  {/* Too few for a percentage: show the counts instead of a
                      bare dash (sc-46067). */}
                  {b.conversionRate == null && b.trialsEnded > 0 ? (
                    <span title={t("growth.smallSample")}>
                      {t("growth.countOf", {
                        count: b.converted,
                        total: b.trialsEnded,
                      })}
                    </span>
                  ) : (
                    rate(b.conversionRate)
                  )}
                </td>
                <td>{money(b.mrrAdded)}</td>
              </tr>
            ))}
            <tr className="growth-table__total">
              <td>{t("growth.trials.allEnded")}</td>
              <td>{endedInPeriod}</td>
              <td>{convertedCount}</td>
              <td>{lapsedCount}</td>
              <td>{rate(convertedOfEnded)}</td>
              <td>{money(mrrAdded)}</td>
            </tr>
          </tbody>
        </table>
        <p className="growth-card__footnote">
          {t("growth.trials.footnote")} {t("growth.correlationalNote")}
        </p>
      </section>
    </GrowthSection>
  );
};

export default GrowthTrialFunnel;
